/**
 * Страница «Data Health» — заполненность показателей паспорта по региону.
 *
 * Читает `GET /api/admin/data-health` (super-admin). Регион берётся бэкендом из
 * запроса, поэтому переключателя региона здесь нет: развёртывание СКО показывает
 * СКО, ЗКО — ЗКО.
 *
 * Что видно по каждому показателю: раздел паспорта, источник данных, таблица и
 * колонки в БД, охват по КАТО (заполнено / ожидается) и число строк.
 *
 * Показатели с `measure = ROWS` привязки к КАТО не имеют — процент для них не
 * считается и рисуется прочерком, а не нулём: неизвестное не должно выглядеть
 * как пустое.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { useUser } from '../../../contexts/UserContext';
import {
  getDataHealth,
  type DataHealthIndicator,
  type DataHealthReport,
  type LevelCounts,
} from '../../../requests/data_health.request';
import { getKatoList, type KatoListItem } from '../../../requests/public.request';

/** Порог «хорошо» — ниже него показатель считается проблемным. */
const GOOD_THRESHOLD = 80;
/** Порог «плохо» — ниже него охват критический. */
const POOR_THRESHOLD = 40;

/** Год отправляем только целиком: иначе промежуточные «2», «20» ловят 422. */
const YEAR_PATTERN = /^\d{4}$/;

const LEVEL_LABEL_KEYS: Record<string, string> = {
  '0': 'dataHealth.levelRegion',
  '1': 'dataHealth.levelDistrict',
  '2': 'dataHealth.levelOkrug',
  '3': 'dataHealth.levelVillage',
  unknown: 'dataHealth.levelUnknown',
};

/**
 * Плоский список КАТО в порядке обхода дерева, с глубиной для отступа.
 * Родитель идёт перед детьми, иначе в выпадающем списке из ~800 позиций
 * невозможно понять, чей это округ.
 */
const flattenHierarchy = (
  items: KatoListItem[],
): { item: KatoListItem; depth: number }[] => {
  const children = new Map<number | null, KatoListItem[]>();
  for (const item of items) {
    const bucket = children.get(item.parent_id);
    if (bucket) bucket.push(item);
    else children.set(item.parent_id, [item]);
  }

  const known = new Set(items.map((i) => i.kato));
  const out: { item: KatoListItem; depth: number }[] = [];
  const walk = (parent: number | null, depth: number) => {
    for (const item of children.get(parent) ?? []) {
      out.push({ item, depth });
      walk(item.kato, depth + 1);
    }
  };
  walk(null, 0);

  // Сироты (родитель вне справочника) иначе исчезли бы из списка молча.
  for (const item of items) {
    if (item.parent_id !== null && !known.has(item.parent_id)) {
      out.push({ item, depth: 0 });
    }
  }
  return out;
};

const barColor = (percent: number): string => {
  if (percent >= GOOD_THRESHOLD) return 'bg-brand-500';
  if (percent >= POOR_THRESHOLD) return 'bg-status-warning';
  return 'bg-status-danger';
};

export const DataHealth = () => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const isKz = i18n.language === 'kz';

  const [report, setReport] = useState<DataHealthReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [year, setYear] = useState<string>('');
  const [kato, setKato] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [katoItems, setKatoItems] = useState<KatoListItem[]>([]);

  const isAdmin = user?.is_admin ?? false;

  const load = useCallback(async (requestedYear: string, requestedKato: string) => {
    setLoading(true);
    setFailed(false);
    try {
      const data = await getDataHealth({
        year: YEAR_PATTERN.test(requestedYear)
          ? Number.parseInt(requestedYear, 10)
          : null,
        kato: requestedKato === '' ? null : Number.parseInt(requestedKato, 10),
      });
      setReport(data);
    } catch {
      setFailed(true);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    void load(year, kato);
  }, [isAdmin, load, year, kato]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    getKatoList()
      .then((res) => {
        if (!cancelled) setKatoItems(res.items);
      })
      // Список территорий — вспомогательный: без него страница работает по
      // региону целиком, поэтому падение фетча не рушит весь отчёт.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const katoOptions = useMemo(() => flattenHierarchy(katoItems), [katoItems]);

  const sources = useMemo(() => {
    if (report === null) return [];
    const seen = new Map<string, string>();
    for (const section of report.sections) {
      for (const indicator of section.indicators) {
        seen.set(
          indicator.source,
          isKz ? indicator.source_kz : indicator.source_ru,
        );
      }
    }
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [report, isKz]);

  const sections = useMemo(() => {
    if (report === null) return [];
    return report.sections
      .map((section) => ({
        ...section,
        indicators: section.indicators.filter((indicator) => {
          if (source !== '' && indicator.source !== source) return false;
          if (!problemsOnly) return true;
          return indicator.percent === null || indicator.percent < GOOD_THRESHOLD;
        }),
      }))
      .filter((section) => section.indicators.length > 0);
  }, [report, source, problemsOnly]);

  if (!user) {
    return <p data-testid="data-health-auth-required">{t('common.loading')}</p>;
  }
  if (!isAdmin) {
    return <p data-testid="data-health-forbidden">{t('adminModules.forbidden')}</p>;
  }

  const noteOf = (indicator: DataHealthIndicator): string | null =>
    isKz ? indicator.note_kz : indicator.note_ru;

  const renderCoverage = (indicator: DataHealthIndicator) => {
    const { percent } = indicator;
    if (percent === null) {
      return (
        <span className="text-text-muted" title={noteOf(indicator) ?? undefined}>
          —
        </span>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <div className="h-2 w-24 shrink-0 overflow-hidden rounded-full bg-surface-muted">
          <div
            className={`h-full ${barColor(percent)}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <span className="tabular-nums">{percent}%</span>
        <span className="whitespace-nowrap text-xs text-text-muted">
          {indicator.filled}/{indicator.expected}
        </span>
      </div>
    );
  };

  const renderTotals = (totals: LevelCounts) =>
    Object.entries(totals).map(([level, count]) => (
      <span key={level} className="whitespace-nowrap">
        {t(LEVEL_LABEL_KEYS[level] ?? 'dataHealth.levelUnknown')}:{' '}
        <b className="tabular-nums">{count}</b>
      </span>
    ));

  return (
    <div className="space-y-4 p-5" data-testid="data-health">
      <div>
        <h1 className="text-2xl font-semibold">{t('dataHealth.title')}</h1>
        <p className="text-sm text-text-muted">{t('dataHealth.subtitle')}</p>
      </div>

      {/*
        Фильтры живут ВНЕ ветки `!loading`: смена года перезапрашивает отчёт, и
        если бы блок размонтировался на время загрузки, поле года исчезало бы
        из-под курсора после первого же введённого символа.
      */}
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-text-muted">{t('dataHealth.year')}</span>
          <input
            type="number"
            inputMode="numeric"
            value={year}
            placeholder={t('dataHealth.allYears')}
            onChange={(event) => setYear(event.target.value)}
            className="h-9 w-32 rounded-md border border-surface-border px-3"
            data-testid="data-health-year"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-text-muted">{t('dataHealth.kato')}</span>
          <select
            value={kato}
            onChange={(event) => setKato(event.target.value)}
            className="h-9 max-w-xs rounded-md border border-surface-border px-3"
            data-testid="data-health-kato"
          >
            <option value="">{t('dataHealth.wholeRegion')}</option>
            {katoOptions.map(({ item, depth }) => (
              <option key={item.kato} value={item.kato}>
                {' '.repeat(depth * 3)}
                {isKz ? item.name_kz : item.name_ru}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-text-muted">{t('dataHealth.source')}</span>
          <select
            value={source}
            onChange={(event) => setSource(event.target.value)}
            className="h-9 rounded-md border border-surface-border px-3"
            data-testid="data-health-source"
          >
            <option value="">{t('dataHealth.allSources')}</option>
            {sources.map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={problemsOnly}
            onChange={(event) => setProblemsOnly(event.target.checked)}
            data-testid="data-health-problems-only"
          />
          {t('dataHealth.problemsOnly')}
        </label>
      </div>

      {loading && (
        <div data-testid="data-health-loading" className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {!loading && failed && (
        <p data-testid="data-health-error">{t('common.dataUnavailable')}</p>
      )}

      {!loading && report !== null && (
        <>
          <Card className="shadow-card" data-testid="data-health-summary">
            <CardContent className="flex flex-wrap items-center gap-x-8 gap-y-3 p-5">
              <div>
                <div
                  className="text-3xl font-semibold tabular-nums"
                  data-testid="data-health-overall"
                >
                  {report.percent === null ? '—' : `${report.percent}%`}
                </div>
                <div className="text-sm text-text-muted">
                  {t('dataHealth.overall')}
                </div>
              </div>
              <div className="text-sm">
                <div className="font-medium" data-testid="data-health-scope-name">
                  {report.kato === null
                    ? isKz
                      ? report.region_name_kz
                      : report.region_name_ru
                    : (isKz ? report.kato_name_kz : report.kato_name_ru) ??
                      String(report.kato)}
                </div>
                {report.kato !== null && (
                  <div className="text-xs text-text-muted">
                    {isKz ? report.region_name_kz : report.region_name_ru}
                  </div>
                )}
                <div className="text-text-muted">
                  {t('dataHealth.coveredOf', {
                    filled: report.filled,
                    expected: report.expected,
                  })}
                </div>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-text-muted">
                {renderTotals(report.kato_totals)}
              </div>
            </CardContent>
          </Card>

          {sections.length === 0 && (
            <p data-testid="data-health-empty">{t('common.noData')}</p>
          )}

          {sections.map((section) => (
            <Card
              key={section.key}
              className="shadow-card"
              data-testid={`data-health-section-${section.key}`}
            >
              <CardContent className="p-0">
                <div className="flex items-center justify-between border-b border-surface-border px-5 py-3">
                  <h2 className="text-base font-semibold">
                    {isKz ? section.name_kz : section.name_ru}
                  </h2>
                  <span className="text-sm tabular-nums text-text-muted">
                    {section.percent === null ? '—' : `${section.percent}%`}
                  </span>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('dataHealth.indicator')}</TableHead>
                      <TableHead>{t('dataHealth.source')}</TableHead>
                      <TableHead>{t('dataHealth.storage')}</TableHead>
                      <TableHead>{t('dataHealth.scope')}</TableHead>
                      <TableHead>{t('dataHealth.coverage')}</TableHead>
                      <TableHead className="text-right">
                        {t('dataHealth.rows')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {section.indicators.map((indicator) => (
                      <TableRow
                        key={indicator.key}
                        data-testid={`data-health-row-${indicator.key}`}
                      >
                        <TableCell className="font-medium">
                          {isKz ? indicator.name_kz : indicator.name_ru}
                          {noteOf(indicator) !== null && (
                            <div className="mt-1 text-xs font-normal text-text-muted">
                              {noteOf(indicator)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {isKz ? indicator.source_kz : indicator.source_ru}
                        </TableCell>
                        <TableCell>
                          <code className="text-xs">{indicator.table}</code>
                          {indicator.columns.length > 0 && (
                            <div className="text-xs text-text-muted">
                              {indicator.columns.join(', ')}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-text-muted">
                          {isKz ? indicator.scope_kz : indicator.scope_ru}
                        </TableCell>
                        <TableCell>{renderCoverage(indicator)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {indicator.rows}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}

          <p className="text-xs text-text-muted" data-testid="data-health-generated">
            {t('dataHealth.generatedAt', {
              value: new Date(report.generated_at).toLocaleString(
                isKz ? 'kk-KZ' : 'ru-RU',
              ),
            })}
          </p>
        </>
      )}
    </div>
  );
};
